import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthProvider';
import { getAuthToken } from '../lib/supabaseClient';
import { DESIGN_PREVIEW, previewUsage } from '../lib/designPreview';
import { apiFetch } from '../lib/apiClient';
import type { SearchUsage } from '../lib/types';

type UsageApiResponse = {
  search_count: number;
  last_reset: string;
  remaining: number;
  limit?: number;
  period_end?: string;
};

/** Survives remounts so the gold badge does not vanish while refetching. */
let usageCache: SearchUsage | null = DESIGN_PREVIEW ? previewUsage : null;
type UsageListener = (usage: SearchUsage) => void;
const usageListeners = new Set<UsageListener>();

function mapUsage(result: UsageApiResponse): SearchUsage {
  const limit = typeof result.limit === 'number' ? result.limit : 30;
  const remaining =
    typeof result.remaining === 'number'
      ? result.remaining
      : Math.max(0, limit - result.search_count);

  return {
    searchCount: result.search_count,
    lastReset: new Date(result.last_reset),
    remainingSearches: remaining,
    limit,
    periodEnd: result.period_end ? new Date(result.period_end) : undefined,
  };
}

export function applyUsageFromServer(result: UsageApiResponse): SearchUsage {
  const next = mapUsage(result);
  usageCache = next;
  usageListeners.forEach(listener => listener(next));
  return next;
}

export function subscribeToUsage(listener: UsageListener): () => void {
  usageListeners.add(listener);
  return () => {
    usageListeners.delete(listener);
  };
}

export function useSearchLimits() {
  const { user } = useAuth();
  const [usage, setUsage] = useState<SearchUsage | null>(usageCache);
  const [loading, setLoading] = useState(!DESIGN_PREVIEW && !usageCache);
  const [error, setError] = useState<string | null>(null);

  const fetchUsage = useCallback(async () => {
    try {
      if (DESIGN_PREVIEW) return;
      if (!user) return;

      if (!usageCache) setLoading(true);

      const result = await apiFetch<UsageApiResponse>(`/api/search-usage/${user.id}`, {
        token: await getAuthToken(),
      });
      const next = applyUsageFromServer(result);
      setUsage(next);
      setError(null);
    } catch (err) {
      console.error('Error fetching search usage:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch search usage');
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Legacy client-side increment — /api/recommend now consumes every pull
   * server-side. Kept for recovery/tests.
   */
  const incrementSearchCount = async () => {
    try {
      if (DESIGN_PREVIEW) return true;
      if (!user || !usage) return false;

      const data = await apiFetch<UsageApiResponse>(`/api/search-usage/${user.id}`, {
        method: 'POST',
        token: await getAuthToken(),
        body: { increment: true },
      });

      const next = applyUsageFromServer(data);
      setUsage(next);
      return true;
    } catch (err) {
      console.error('Error incrementing search count:', err);
      throw err;
    }
  };

  useEffect(() => subscribeToUsage(setUsage), []);

  useEffect(() => {
    if (user && !DESIGN_PREVIEW) {
      void fetchUsage();
    }
  }, [user?.id, fetchUsage]);

  return {
    usage,
    loading,
    error,
    incrementSearchCount,
    refreshUsage: fetchUsage,
    setUsageFromServer: (result: UsageApiResponse) => {
      const next = applyUsageFromServer(result);
      setUsage(next);
    },
  };
}
